import type { BoardState } from './board';
import type { Direction, PlayerState } from './types';
import type { EventLog } from './eventLog';
import { SCORING, SCAN_REVEAL_MS } from './config';
import { scoreCorrect, gauntletScore, isGauntlet } from './scoring';
import { neighborId, DIRS } from './grid';

export interface ActionEffect {
  moved: boolean;
  ate: 'correct' | 'decoy' | null;
  timeRefundMs: number;
  purgeCell: number | null; // a correct eat stuns hazards adjacent to this cell
}

const NONE: ActionEffect = { moved: false, ate: null, timeRefundMs: 0, purgeCell: null };

/** A cell you may stand on / move onto (not a wall, not a live armed lockout). */
export function passable(state: BoardState, id: number): boolean {
  const c = state.cells[id];
  if (c.kind === 'wall') return false;
  if (c.armed && !c.eaten) return false;
  return true;
}

function eatCorrect(state: BoardState, p: PlayerState, cellId: number, nowMs: number, log: EventLog): ActionEffect {
  const cell = state.cells[cellId];
  if (cell.eaten) return NONE;
  cell.eaten = true;
  cell.eatenBy = p.id;
  p.combo += 1;
  // CHOMP SURGE (temp offense): a correct commit arms a brief window where the next hazard contact is
  // PURGED instead of lethal (see resolveHazardHits). Integer clock, no RNG — determinism-safe.
  p.surgeUntilMs = nowMs + SCORING.surgeDurationMs;
  const base = scoreCorrect(p.combo);
  const gauntlet = isGauntlet(p.armedTraversals);
  const pts = gauntlet ? gauntletScore(base) : base;
  p.score += pts;
  p.eatenCorrect += 1;
  log.push('munchCorrect', nowMs, { player: p.id, cell: cellId, value: pts });
  log.push('combo', nowMs, { player: p.id, value: p.combo });
  if (gauntlet) log.push('gauntlet', nowMs, { player: p.id, cell: cellId, value: pts });
  p.armedTraversals = 0;
  return { moved: false, ate: 'correct', timeRefundMs: SCORING.timeRefundMsPerCorrect, purgeCell: cellId };
}

function eatDecoy(state: BoardState, p: PlayerState, cellId: number, nowMs: number, log: EventLog): ActionEffect {
  const cell = state.cells[cellId];
  if (cell.eaten) return NONE;
  cell.eaten = true;
  cell.eatenBy = p.id;
  p.score -= SCORING.decoyPenalty;
  p.eatenDecoy += 1;
  p.combo = 0;
  p.armedTraversals = 0;
  p.stunnedUntilMs = nowMs + SCORING.decoyStunMs;
  log.push('munchDecoy', nowMs, { player: p.id, cell: cellId, value: -SCORING.decoyPenalty });
  log.push('stun', nowMs, { player: p.id, value: SCORING.decoyStunMs });
  return { moved: false, ate: 'decoy', timeRefundMs: 0, purgeCell: null };
}

/**
 * Step one cell. Movement never evaluates the answer under the player. Leaving an un-eaten
 * decoy ARMS it (the pass-through risk); CHOMP/munchHere is the only answer-commit action.
 */
export function moveAndMaybeEat(
  state: BoardState,
  p: PlayerState,
  dir: Direction,
  nowMs: number,
  log: EventLog,
): ActionEffect {
  const dest = neighborId(p.pos, dir, state.rows, state.cols);
  if (dest === null || !passable(state, dest)) {
    log.push('blocked', nowMs, { player: p.id, cell: dest ?? -1 });
    return NONE;
  }
  const cur = state.cells[p.pos];
  if (cur.kind === 'decoy' && !cur.eaten && !cur.armed) {
    cur.armed = true;
    cur.armedUntilMs = nowMs + SCORING.armDurationMs;
    p.armedTraversals += 1;
    log.push('passArm', nowMs, { player: p.id, cell: cur.id });
  }
  p.pos = dest;
  log.push('move', nowMs, { player: p.id, cell: dest });
  return { ...NONE, moved: true };
}

/** Explicit CHOMP of the cell under the player: the only way to evaluate or eat any answer. */
export function munchHere(state: BoardState, p: PlayerState, nowMs: number, log: EventLog): ActionEffect {
  const cell = state.cells[p.pos];
  if (cell.eaten || cell.kind === 'wall') return NONE;
  return cell.kind === 'correct' ? eatCorrect(state, p, p.pos, nowMs, log) : eatDecoy(state, p, p.pos, nowMs, log);
}

/**
 * The SCAN information verb (verify B1): spend one scan to reveal the true status of the cell
 * under the player + its orthogonal neighbors for a short window — agency for an unknowing player.
 */
export function applyScan(state: BoardState, p: PlayerState, nowMs: number, log: EventLog): ActionEffect {
  if (p.scansLeft <= 0) {
    log.push('blocked', nowMs, { player: p.id, value: 0 });
    return NONE;
  }
  p.scansLeft -= 1;
  const reveal = (id: number) => {
    const c = state.cells[id];
    if (c.kind !== 'wall') c.revealedUntilMs = Math.max(c.revealedUntilMs, nowMs + SCAN_REVEAL_MS);
  };
  reveal(p.pos);
  for (const d of DIRS) {
    const nb = neighborId(p.pos, d, state.rows, state.cols);
    if (nb !== null) reveal(nb);
  }
  log.push('scan', nowMs, { player: p.id, cell: p.pos, value: p.scansLeft });
  return NONE;
}
